import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import {
  FaPalette,
  FaCode,
  FaExternalLinkAlt,
  FaArrowRight,
  FaGlobe,
  FaServer,
  FaLock,
  FaCheck,
  FaTools,
  FaBookOpen,
  FaTimes,
  FaUserCog,
} from "react-icons/fa";
import SEO from "../../components/common/SEO";
import Breadcrumb from "../../components/common/Breadcrumb";
import PageSection from "../../components/common/PageSection";
import { CODEJBORG } from "../../config/constants";
import arvixLogo from "../../assets/arvix/arvix_logo.png";
import codejborgLogo from "../../assets/collab/codejborg_logo.webp";
import greenBiotechHome from "../../assets/collab/green_biotech_home.webp";
import greenBiotechProducts from "../../assets/collab/green_biotech_products.webp";
import greenBiotechCertificates from "../../assets/collab/green_biotech_certificates.webp";
import erpDashboard from "../../assets/collab/green_biotech_erp_dashboard.webp";
import erpSalesOrders from "../../assets/collab/green_biotech_erp_sales_orders.webp";
import erpTrialBalance from "../../assets/collab/green_biotech_erp_trial_balance.webp";
import draftiqHome from "../../assets/collab/draftiq_home.webp";
import draftiqStudio from "../../assets/collab/draftiq_studio.webp";
import draftiqFinder from "../../assets/collab/draftiq_finder.webp";
import alNoorHome from "../../assets/collab/al_noor_hospital_home.webp";
import alNoorDoctor from "../../assets/collab/al_noor_hospital_doctor.webp";
import alNoorDepartments from "../../assets/collab/al_noor_hospital_departments.webp";
import alNoorPackages from "../../assets/collab/al_noor_hospital_packages.webp";
import greenBiotechAbout from "../../assets/collab/green_biotech_about.webp";
import erpSalesmanDashboard from "../../assets/collab/green_biotech_erp_salesman_dashboard.webp";
import erpProducts from "../../assets/collab/green_biotech_erp_products.webp";
import erpJournalEntries from "../../assets/collab/green_biotech_erp_journal_entries.webp";
import erpFinancialReports from "../../assets/collab/green_biotech_erp_financial_reports.webp";
import draftiqShop from "../../assets/collab/draftiq_shop.webp";
import draftiqProfile from "../../assets/collab/draftiq_profile.webp";
import draftiqBundle from "../../assets/collab/draftiq_bundle.webp";
import draftiqCart from "../../assets/collab/draftiq_cart.webp";
import alNoorDoctors from "../../assets/collab/al_noor_hospital_doctors.webp";
import alNoorBook from "../../assets/collab/al_noor_hospital_book.webp";
import alNoorBookStart from "../../assets/collab/al_noor_hospital_book_start.webp";
import alNoorServicesSelect from "../../assets/collab/al_noor_hospital_services_select.webp";
import alNoorServiceRequest from "../../assets/collab/al_noor_hospital_service_request.webp";
import alNoorAdminDashboard from "../../assets/collab/al_noor_hospital_admin_dashboard.webp";
import alNoorAdminAppointments from "../../assets/collab/al_noor_hospital_admin_appointments.webp";
import alNoorAdminVisitors from "../../assets/collab/al_noor_hospital_admin_visitors.webp";
import draftiqProduct from "../../assets/collab/draftiq_product.webp";
import greenBiotechCareer from "../../assets/collab/green_biotech_career.webp";

// Case studies for every project live on the CodeJBorg portfolio.
const CASE_STUDY_BASE = "https://junaidul.pro.bd/project";

// `url` is only set for projects that are publicly hosted. Green Biotech ERP
// is private client software and must never get a public link here.
const PROJECTS = [
  {
    id: "green-biotech",
    title: "Green Biotech",
    client: "Green Biotech BD · Veterinary medicines",
    category: "website",
    status: "live",
    description:
      "Marketing and ordering website for Green Biotech BD, a supplier of verified veterinary medicines for poultry, cattle, aquaculture and pets. Visitors browse and search the catalog, read product details and place cash-on-delivery orders that reach the company by email.",
    highlights: [
      "Catalog by animal category with supplier, type and stock filters, sorting and live navbar search",
      "Product pages with active ingredients, indications, usage, packaging, price and stock",
      "Browser-saved cart and cash-on-delivery checkout that emails the order with a generated order ID",
      "Company profile, certificates, job listings and contact form",
      "Sitemap, Organization/Product JSON-LD and Open Graph tags for search",
    ],
    tech: ["Next.js 15", "React 19", "TypeScript", "Tailwind CSS v4", "EmailJS", "Vercel"],
    url: "https://www.greenbiotechbd.com/",
    caseStudy: "green-biotech",
    shots: [
      { src: greenBiotechHome, caption: "Home page" },
      { src: greenBiotechProducts, caption: "Product catalog by animal category" },
      { src: greenBiotechAbout, caption: "About: mission and values" },
      { src: greenBiotechCertificates, caption: "Certificates" },
      { src: greenBiotechCareer, caption: "Careers" },
    ],
  },
  {
    id: "green-biotech-erp",
    title: "Green Biotech ERP",
    client: "Green Biotech BD · B2B distribution",
    category: "web-software",
    status: "private",
    description:
      "Private B2B distribution ERP for a veterinary-medicine distributor: shops grouped into territories owned by salesmen, with sales, procurement, stock, employees and ledger-based accounting. A ground-up rebuild of an earlier Spring Boot version, with the ledger as the single source of truth.",
    highlights: [
      "Sales order → invoice → collection workflow with returns, refunds and server-rendered PDFs",
      "Purchase orders and receiving; append-only stock movements with row locks so orders can't oversell",
      "Journal entries, ledger, trial balance and reports; every event posts exactly once",
      "Declarative state machines reject illegal document transitions",
      "Territory-scoped roles, admin and salesman dashboards, audit trail and tested business rules",
    ],
    tech: ["React 19", "Vite", "TypeScript", "Express 5", "Drizzle ORM", "PostgreSQL", "Vitest"],
    caseStudy: "green-biotech-erp",
    shots: [
      { src: erpDashboard, caption: "Admin dashboard" },
      { src: erpSalesmanDashboard, caption: "Salesman dashboard (territory-scoped)" },
      { src: erpSalesOrders, caption: "Sales orders" },
      { src: erpProducts, caption: "Products and stock" },
      { src: erpJournalEntries, caption: "Journal entries" },
      { src: erpTrialBalance, caption: "Trial balance" },
      { src: erpFinancialReports, caption: "Financial reports" },
    ],
  },
  {
    id: "draftiq",
    title: "Draftiq",
    client: "Draftiq · NFC cards & mobile accessories",
    category: "web-software",
    status: "live",
    description:
      "Online shop for NFC smart cards and mobile accessories (chargers, cables, covers and audio) in English and Bangla. Shoppers design their own NFC card with a live 3D preview, find matching accessories for their phone and check out on one page; an admin panel runs orders, products, promotions and deals.",
    highlights: [
      "NFC Card Studio: choose from 24 designs, set up the tap-to-share profile, preview in 3D and order",
      "Public tap profiles with call, WhatsApp and save-contact actions",
      "Mobile Finder and Bundle & Save match chargers, cables and covers to your phone",
      "One-page checkout with promo codes and cash on delivery, bKash, Nagad or bank transfer",
      "Order tracking, wishlist, reviews, deal of the day and an admin dashboard; one-tap demo logins",
    ],
    tech: ["React 18", "Vite", "Tailwind CSS v4", "Three.js", "Express", "PostgreSQL (Neon)", "Vercel"],
    url: "https://draftiq-shop.vercel.app/",
    caseStudy: "draftiq",
    shots: [
      { src: draftiqHome, caption: "Home page" },
      { src: draftiqShop, caption: "Shop with filters" },
      { src: draftiqProduct, caption: "Product page" },
      { src: draftiqStudio, caption: "NFC Card Studio" },
      { src: draftiqProfile, caption: "Tap profile setup" },
      { src: draftiqFinder, caption: "Mobile Finder" },
      { src: draftiqBundle, caption: "Bundle & Save" },
      { src: draftiqCart, caption: "One-page checkout" },
    ],
  },
  {
    id: "al-noor-hospital",
    title: "Al Noor Specialised Hospital",
    client: "Al Noor Specialised Hospital · Cox's Bazar",
    category: "website-admin",
    status: "live",
    description:
      "Hospital website and admin panel. Patients browse 28 doctors across 6 departments, see who is in chamber today and book a 15-minute serial online, or pick individual tests and request them; staff run everything from a protected dashboard.",
    highlights: [
      "Four-step booking (department, doctor, time, details) on 15-minute serials, including till-last-patient chambers",
      "Custom service requests: pick individual tests, see the running total and send one request",
      "Doctor profiles with credentials, availability badges and weekly chamber schedules",
      "Admin dashboard with KPIs, booking pipeline and today's schedule; doctors, departments, packages and registers",
      "Email on confirmation and first-party, cookie-free visitor analytics",
    ],
    tech: ["React 19", "Vite", "TypeScript", "shadcn/ui", "Express 5", "Prisma", "PostgreSQL (Neon)", "Resend"],
    url: "https://www.alnoorsh.com/",
    caseStudy: "al-noor-specialised-hospital",
    shots: [
      { src: alNoorHome, caption: "Home page" },
      { src: alNoorDoctors, caption: "Find a consultant" },
      { src: alNoorDoctor, caption: "Doctor profile and schedule" },
      { src: alNoorBookStart, caption: "Booking: choose a department" },
      { src: alNoorBook, caption: "Booking: 15-minute serials" },
      { src: alNoorServicesSelect, caption: "Custom service selection" },
      { src: alNoorServiceRequest, caption: "Service request form" },
      { src: alNoorDepartments, caption: "Departments" },
      { src: alNoorPackages, caption: "Health checkup packages" },
      { src: alNoorAdminDashboard, caption: "Admin dashboard (demo data)" },
      { src: alNoorAdminAppointments, caption: "Admin appointments (demo data)" },
      { src: alNoorAdminVisitors, caption: "Visitor analytics (demo data)" },
    ],
  },
];

// Same three categories, names and project mapping as the Works page on the
// CodeJBorg portfolio (https://junaidul.pro.bd/projects), taken from the
// CodeJBorg packages. Keep both in step.
const CATEGORIES = [
  {
    id: "website",
    label: "Website",
    blurb: "Fast, polished sites with no back office to run.",
    icon: FaGlobe,
  },
  {
    id: "website-admin",
    label: "Website + Admin Panel",
    blurb: "Dynamic websites the owner updates from a secure admin panel.",
    icon: FaUserCog,
  },
  {
    id: "web-software",
    label: "Web-based Software",
    blurb: "Business software and platforms with roles, workflows and data.",
    icon: FaServer,
  },
];
const CATEGORY_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
const countIn = (id) => PROJECTS.filter((p) => p.category === id).length;

const STATUS = {
  live: {
    label: "Live",
    className: "bg-white/95 text-emerald-700",
    dot: "bg-emerald-500 motion-safe:animate-pulse",
  },
  private: {
    label: "Private client software",
    className: "bg-designer-dark/85 text-white",
    icon: FaLock,
  },
  development: {
    label: "In development",
    className: "bg-designer-accent text-designer-dark",
    icon: FaTools,
  },
};

// Fades a block up once when it scrolls into view. Users who prefer reduced
// motion get the content immediately.
const Reveal = ({ children, className = "", as: Tag = "div", ...rest }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.12 });
  return (
    <Tag
      ref={ref}
      className={`${className} motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${
        inView ? "opacity-100 translate-y-0" : "motion-safe:opacity-0 motion-safe:translate-y-6"
      }`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

// Partnership lockup: both real logos, side by side, joined by the brand ×,
// each captioned with what that studio brings.
const PartnershipLockup = () => (
  <figure
    className="mx-auto w-full max-w-2xl rounded-3xl bg-white/90 backdrop-blur border border-gray-100 shadow-lg px-4 py-6 sm:px-10 sm:py-8"
    aria-label="Arvix Creative and CodeJBorg partnership"
  >
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-8">
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-12 sm:h-16 items-center">
          <img
            src={arvixLogo}
            alt="Arvix Creative"
            width="1169"
            height="446"
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>
        <span className="text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase text-gray-400">
          Brand identity
        </span>
      </div>
      <span
        aria-hidden="true"
        className="-mt-7 flex h-9 w-9 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-designer-primary text-white shadow-md shadow-red-200"
      >
        <FaTimes className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </span>
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-12 sm:h-16 items-center">
          <img
            src={codejborgLogo}
            alt="CodeJBorg"
            width="560"
            height="114"
            className="h-6 sm:h-10 w-auto object-contain"
          />
        </div>
        <span className="text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase text-gray-400">
          UI/UX &amp; Build
        </span>
      </div>
    </div>
  </figure>
);

const RoleCard = ({ step, icon: Icon, name, role, items, href }) => {
  const body = (
    <>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-2xl bg-red-50 text-designer-primary flex items-center justify-center text-xl transition-colors group-hover:bg-designer-primary group-hover:text-white">
          <Icon aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-gray-400">Step {step}</p>
          <h3 className="font-display font-bold text-designer-dark text-lg flex items-center gap-2">
            {name}
            {href && (
              <FaExternalLinkAlt
                aria-hidden="true"
                className="w-3 h-3 text-gray-300 group-hover:text-designer-primary transition-colors"
              />
            )}
          </h3>
          <p className="text-sm text-gray-500">{role}</p>
        </div>
      </div>
      <ul className="mt-4 flex flex-wrap gap-1.5" aria-label={`${name} handles`}>
        {items.map((item) => (
          <li
            key={item}
            className="px-2.5 py-1 rounded-full bg-gray-50 border border-gray-100 text-[11px] font-medium text-gray-600"
          >
            {item}
          </li>
        ))}
      </ul>
    </>
  );

  const className =
    "group block h-full rounded-2xl bg-white border border-gray-100 shadow-sm p-5 sm:p-6 transition-all duration-300 hover:shadow-lg hover:border-designer-primary/30 motion-safe:hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2";

  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} aria-label={`${name}, opens in a new tab`}>
      {body}
    </a>
  ) : (
    <div className={className}>{body}</div>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase shadow ${s.className}`}
    >
      {s.dot && <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
      {Icon && <Icon aria-hidden="true" className="w-2.5 h-2.5" />}
      {s.label}
    </span>
  );
};

// Real product screenshots in a minimal browser frame, with thumbnails to
// flip between views.
const ProjectGallery = ({ project }) => {
  const [active, setActive] = useState(0);
  const shot = project.shots[active];

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden border border-gray-200/80 bg-gray-100 shadow-md transition-shadow duration-300 group-hover:shadow-xl">
        <div className="h-6 bg-[#161616] flex items-center gap-1.5 px-3" aria-hidden="true">
          <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
          <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
          <span className="w-2 h-2 rounded-full bg-[#28C840]" />
        </div>
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            key={shot.src}
            src={shot.src}
            alt={`${project.title}: ${shot.caption}`}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-top motion-safe:animate-fade-in motion-safe:transition-transform motion-safe:duration-700 group-hover:scale-[1.03]"
          />
          <div className="absolute top-3 right-3">
            <StatusBadge status={project.status} />
          </div>
        </div>
      </div>

      {project.shots.length > 1 && (
        <div className={`mt-3 grid ${project.shots.length > 4 ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-3"} gap-2 sm:gap-3`} role="group" aria-label={`${project.title} screenshots`}>
          {project.shots.map((s, i) => (
            <button
              key={s.caption}
              type="button"
              onClick={() => setActive(i)}
              aria-pressed={i === active}
              aria-label={`Show ${s.caption}`}
              className={`relative aspect-[16/10] overflow-hidden rounded-lg border-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2 ${
                i === active
                  ? "border-designer-primary shadow-md"
                  : "border-transparent opacity-70 hover:opacity-100 hover:border-red-200"
              }`}
            >
              <img src={s.src} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        Showing {shot.caption}
      </p>
    </div>
  );
};

const ProjectRow = ({ project, reverse }) => {
  const { icon: TypeIcon, label: typeLabel } = CATEGORY_BY_ID[project.category];

  return (
    <Reveal
      as="article"
      className="group grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center rounded-3xl bg-white/80 backdrop-blur border border-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-300 p-4 sm:p-6 lg:p-8"
      aria-labelledby={`${project.id}-title`}
    >
      <div className={reverse ? "lg:order-2" : ""}>
        <ProjectGallery project={project} />
      </div>

      <div className={`flex flex-col ${reverse ? "lg:order-1" : ""}`}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-designer-primary text-[10px] font-bold tracking-wider uppercase">
            <TypeIcon aria-hidden="true" className="w-2.5 h-2.5" />
            {typeLabel}
          </span>
          <span className="text-xs text-gray-500">{project.client}</span>
        </div>

        <h4
          id={`${project.id}-title`}
          className="font-display font-bold text-designer-dark text-2xl md:text-3xl leading-tight group-hover:text-designer-primary transition-colors"
        >
          {project.title}
        </h4>
        <p className="mt-3 text-gray-600 leading-relaxed">{project.description}</p>

        <ul className="mt-4 space-y-2">
          {project.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2.5 text-sm text-gray-600">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-50 text-designer-primary">
                <FaCheck aria-hidden="true" className="w-2 h-2" />
              </span>
              {h}
            </li>
          ))}
        </ul>

        <ul className="mt-5 flex flex-wrap gap-1.5" aria-label="Tech stack">
          {project.tech.map((t) => (
            <li
              key={t}
              className="px-2.5 py-1 bg-gray-50 text-gray-600 text-[11px] rounded-md font-medium border border-gray-100"
            >
              {t}
            </li>
          ))}
        </ul>

        <p className="mt-5 text-[10px] font-bold tracking-wider text-gray-400 uppercase">
          Logo · <span className="text-designer-primary">Arvix Creative</span> &nbsp;/&nbsp; Design &amp; Build ·{" "}
          <span className="text-designer-dark">{CODEJBORG.name}</span>
        </p>

        <div className="mt-5 pt-5 border-t border-gray-100 flex flex-wrap items-center gap-3">
          {project.url ? (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-designer-primary text-white text-sm font-bold shadow-md shadow-red-200 hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2"
              aria-label={`Visit ${project.title}, opens in a new tab`}
            >
              Visit live site
              <FaExternalLinkAlt aria-hidden="true" className="w-3 h-3" />
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-50 border border-gray-100 text-xs font-medium text-gray-500">
              {project.status === "development" ? (
                <>
                  <FaTools aria-hidden="true" className="w-3 h-3" /> In development, not public yet
                </>
              ) : (
                <>
                  <FaLock aria-hidden="true" className="w-3 h-3" /> Private client software, no public link
                </>
              )}
            </span>
          )}
          <a
            href={`${CASE_STUDY_BASE}/${project.caseStudy}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group/cs inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gray-200 text-sm font-bold text-designer-dark hover:border-designer-primary hover:text-designer-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2"
            aria-label={`Read the ${project.title} case study, opens in a new tab`}
          >
            <FaBookOpen aria-hidden="true" className="w-3 h-3" />
            Case study
            <FaArrowRight aria-hidden="true" className="w-3 h-3 motion-safe:transition-transform group-hover/cs:translate-x-1" />
          </a>
        </div>
      </div>
    </Reveal>
  );
};

const Collab = () => {
  // ?category=<id> keeps the chosen tab in the URL (shareable, survives reload).
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = CATEGORY_BY_ID[searchParams.get("category")] ? searchParams.get("category") : "all";
  const setFilter = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setSearchParams(next, { replace: true, preventScrollReset: true });
  };
  const groups = CATEGORIES.filter((c) => filter === "all" || c.id === filter)
    .map((c) => ({ ...c, projects: PROJECTS.filter((p) => p.category === c.id) }))
    .filter((g) => g.projects.length > 0);
  const visibleCount = groups.reduce((n, g) => n + g.projects.length, 0);
  const tabs = [{ id: "all", label: "All", count: PROJECTS.length }, ...CATEGORIES.map((c) => ({ ...c, count: countIn(c.id) }))];
  const liveCount = PROJECTS.filter((p) => p.status === "live").length;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Arvix Creative × CodeJBorg",
    description:
      "Projects where Arvix Creative designed the logo and brand identity, and CodeJBorg designed and built the website or software: Green Biotech, Green Biotech ERP, Draftiq, and Al Noor Specialised Hospital.",
    url: "https://arvixcreative.com/collab",
  };

  return (
    <>
      <SEO
        title="Arvix Creative × CodeJBorg - Design Meets Development"
        description="See what Arvix Creative and CodeJBorg build together: Arvix Creative designs the logo and brand identity, CodeJBorg designs and builds the product. Real websites and software, from Green Biotech to Al Noor Specialised Hospital."
        keywords="arvix creative codejborg, design and development studio, green biotech website, green biotech erp, draftiq, al noor specialised hospital"
        url="https://arvixcreative.com/collab"
        structuredData={structuredData}
      />
      <div className="pt-24 pb-6 bg-white">
        <Breadcrumb items={[{ label: "Collab" }]} />
      </div>

      <PageSection className="pt-6 pb-16 md:pb-24 min-h-screen" gradient="none" overflowHidden={false}>
        <div className="w-full relative z-10">
          {/* Header + partnership lockup */}
          <header className="text-center mb-12 md:mb-16">
            <div className="inline-block mb-6 px-4 py-1.5 rounded-full bg-red-100/50 border border-red-200/50">
              <p className="text-xs font-bold tracking-[0.25em] text-designer-primary uppercase">Collaboration</p>
            </div>
            <PartnershipLockup />
            <h1 className="mt-10 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance">
              <span className="sr-only">Arvix Creative × CodeJBorg: </span>
              Design Meets{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-designer-primary to-red-600">
                Development
              </span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Arvix Creative designs the logo and brand identity.
              CodeJBorg designs and builds: UI/UX, frontend, backend, database, deployment, and CI/CD.
            </p>
          </header>

          {/* Who does what */}
          <section aria-labelledby="roles-heading" className="mb-14 md:mb-20">
            <h2 id="roles-heading" className="sr-only">Who does what</h2>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch gap-4 md:gap-6">
              <Reveal>
                <RoleCard
                  step={1}
                  icon={FaPalette}
                  name="Arvix Creative"
                  role="Logo & brand identity"
                  items={["Logo", "Brand identity"]}
                />
              </Reveal>
              <div className="flex items-center justify-center" aria-hidden="true">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-100 shadow-sm text-designer-primary rotate-90 md:rotate-0">
                  <FaArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
              <Reveal>
                <RoleCard
                  step={2}
                  icon={FaCode}
                  name={CODEJBORG.name}
                  role="Design & build"
                  items={["UI/UX", "Frontend", "Backend", "Database", "Deployment", "CI/CD"]}
                  href={CODEJBORG.url}
                />
              </Reveal>
            </div>

            <dl className="mt-6 grid grid-cols-3 divide-x divide-gray-100 rounded-2xl bg-white border border-gray-100 shadow-sm text-center">
              {[
                [PROJECTS.length, "Projects together"],
                [liveCount, "Live websites"],
                [countIn("web-software"), "Web-based software"],
              ].map(([value, label]) => (
                <div key={label} className="flex flex-col px-2 py-4 sm:py-5">
                  <dt className="text-[10px] sm:text-xs font-bold tracking-wider uppercase text-gray-400">{label}</dt>
                  <dd className="order-first font-display text-2xl sm:text-3xl font-bold text-designer-dark">{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Projects */}
          <section aria-labelledby="projects-heading">
            <div className="mb-8">
              <p className="text-xs font-bold tracking-[0.25em] text-designer-primary uppercase mb-2">Selected work</p>
              <h2 id="projects-heading" className="text-3xl md:text-4xl font-display font-bold text-designer-dark">
                Built together
              </h2>
              <div
                role="group"
                aria-label="Filter projects by CodeJBorg package"
                className="mt-5 flex flex-wrap gap-2"
              >
                {tabs.map((t) => {
                  const active = filter === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setFilter(t.id)}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs sm:text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary focus-visible:ring-offset-2 ${
                        active
                          ? "bg-designer-primary border-designer-primary text-white shadow-md shadow-red-200"
                          : "bg-white border-gray-200 text-gray-600 hover:border-designer-primary hover:text-designer-primary"
                      }`}
                    >
                      {Icon && <Icon aria-hidden="true" className="w-3 h-3" />}
                      {t.label}
                      <span
                        className={`min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[10px] leading-none ${
                          active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {t.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="sr-only" aria-live="polite">
              Showing {visibleCount} project{visibleCount === 1 ? "" : "s"}
              {filter === "all" ? "" : ` in ${CATEGORY_BY_ID[filter].label}`}
            </p>

            <div className="space-y-12 md:space-y-16">
              {groups.map((group) => {
                const Icon = group.icon;
                return (
                  <section key={group.id} aria-labelledby={`cat-${group.id}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-designer-primary">
                        <Icon aria-hidden="true" className="w-4 h-4" />
                      </span>
                      <div className="min-w-0">
                        <h3 id={`cat-${group.id}`} className="font-display font-bold text-designer-dark text-xl md:text-2xl leading-tight">
                          {group.label}{" "}
                          <span className="font-sans text-xs font-bold tracking-wider uppercase text-gray-400 align-middle">
                            {group.projects.length} project{group.projects.length === 1 ? "" : "s"}
                          </span>
                        </h3>
                        <p className="text-sm text-gray-500">{group.blurb}</p>
                      </div>
                    </div>
                    <div className="space-y-6 md:space-y-8">
                      {group.projects.map((project, i) => (
                        <ProjectRow key={project.id} project={project} reverse={i % 2 === 1} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </section>

          {/* Footer CTA */}
          <Reveal className="mt-14 md:mt-20">
            <div className="relative overflow-hidden rounded-3xl bg-designer-dark text-white px-6 py-10 sm:px-12 sm:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div
                aria-hidden="true"
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-designer-primary/30 blur-3xl"
              />
              <div className="relative">
                <h2 className="font-display text-2xl md:text-3xl font-bold">Have a brand and a product in mind?</h2>
                <p className="mt-2 text-white/70 max-w-xl">
                  Arvix Creative shapes the identity, CodeJBorg builds the product.
                </p>
              </div>
              <div className="relative flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-designer-primary text-white text-sm font-bold shadow-lg hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-designer-dark"
                >
                  Start a project
                  <FaArrowRight aria-hidden="true" className="w-3 h-3" />
                </Link>
                <a
                  href="https://junaidul.pro.bd/projects"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/25 text-white text-sm font-bold hover:border-white hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  See more CodeJBorg work
                  <FaExternalLinkAlt aria-hidden="true" className="w-3 h-3" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </PageSection>
    </>
  );
};

export default Collab;
